/**
 * ABP main CTA (sections/abp-main-cta.liquid) behavior: the lower buy box's
 * media carousel + desktop thumbnails, and variant radio sync.
 *
 * This carousel differs from the shared PDP hero one (its own node ids, an
 * `is-active` dot class, drag-free thumbnails, dots rebuilt on `reInit`), so
 * it keeps its own script rather than booting assets/rtnu-pdp-hero.js.
 *
 * Boots once per `[data-rtnu-abp-main-cta]` root. Reads:
 *   data-section-id      Shopify section id (carousel/thumbnail node ids)
 *   data-form-id         product form id
 *   data-currency-symbol currency symbol for per-stick pricing
 */
(() => {
  if (window.__rtnuAbpMainCtaBound) return;
  window.__rtnuAbpMainCtaBound = true;

  const isDesktop = () => window.innerWidth > 768;

  const initCarousel = (root, sectionId) => {
    const mainNode = root.querySelector(`#pdp-main-carousel2-${sectionId}`);
    const thumbsNode = root.querySelector(`#pdp-main-thumbnails2-${sectionId}`);
    const viewport = mainNode?.querySelector('.embla__viewport');
    if (!mainNode || !viewport || typeof EmblaCarousel !== 'function') return null;

    const dots = mainNode.querySelector('.embla__dots');
    if (typeof mainNode._emblaCleanup === 'function') mainNode._emblaCleanup();

    let main = EmblaCarousel(viewport, { align: 'start', containScroll: 'trimSnaps', slidesToScroll: 1 });
    let thumbs = null;
    const thumbHandlers = [];
    mainNode._emblaApi = main;

    const destroyThumbs = () => {
      thumbHandlers.forEach(({ slide, handler }) => slide.removeEventListener('click', handler));
      thumbHandlers.length = 0;
      thumbs?.destroy();
      thumbs = null;
      thumbsNode?.querySelectorAll('.embla__slide').forEach((slide) => slide.classList.remove('is-selected'));
    };

    const rebuildDots = () => {
      if (!dots || !main) return;
      dots.replaceChildren(
        ...main.scrollSnapList().map((_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'embla__dot';
          dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
          dot.addEventListener('click', () => main.scrollTo(index));
          return dot;
        })
      );
    };

    const updateDots = () => {
      if (!dots || !main) return;
      const selected = main.selectedScrollSnap();
      dots.querySelectorAll('.embla__dot').forEach((dot, index) => dot.classList.toggle('is-active', index === selected));
    };

    const updateThumbs = () => {
      if (!main || !thumbsNode) return;
      const selected = main.selectedScrollSnap();
      thumbsNode.querySelectorAll('.embla__slide').forEach((slide, index) => slide.classList.toggle('is-selected', index === selected));
      thumbs?.scrollTo(selected);
    };

    const initThumbs = () => {
      if (!main || !thumbsNode || !isDesktop()) {
        destroyThumbs();
        return;
      }
      if (thumbs) return;

      const thumbsViewport = thumbsNode.querySelector('.embla__viewport');
      if (!thumbsViewport) return;

      thumbs = EmblaCarousel(thumbsViewport, { align: 'start', containScroll: 'trimSnaps', dragFree: true });
      thumbsNode.querySelectorAll('.embla__slide').forEach((slide, index) => {
        const handler = () => main?.scrollTo(index);
        slide.addEventListener('click', handler);
        thumbHandlers.push({ slide, handler });
      });
    };

    main.on('select', () => {
      updateDots();
      updateThumbs();
    });
    main.on('reInit', () => {
      rebuildDots();
      updateDots();
      updateThumbs();
    });

    rebuildDots();
    initThumbs();
    updateDots();
    updateThumbs();

    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (!main) return;
        main.reInit();
        initThumbs();
        updateDots();
        updateThumbs();
      }, 150);
    };
    window.addEventListener('resize', onResize);

    mainNode._emblaCleanup = () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
      destroyThumbs();
      main?.destroy();
      main = null;
      delete mainNode._emblaApi;
    };

    return mainNode;
  };

  const initVariants = (root, form, sectionId, currencySymbol) => {
    const productId = form.querySelector('[name="id"]');
    const slides = root.querySelectorAll(`#pdp-main-carousel2-${sectionId} .embla__slide`);
    const mainNode = root.querySelector(`#pdp-main-carousel2-${sectionId}`);

    form.querySelectorAll('.rtnu-product__variants .rtnu-option').forEach((option) => {
      const radio = option.querySelector('input[type="radio"]');
      const singlePrice = option.querySelector('.rtnu-product__form-item-single-cost-price');
      if (!radio) return;

      // Per-stick price for pouch products.
      const { priceRaw, itemCount } = radio.dataset;
      if (singlePrice && itemCount) {
        singlePrice.textContent = `${currencySymbol}${(priceRaw / itemCount).toFixed(2)}`;
      }

      option.addEventListener('change', (event) => {
        const variantId = event.target.value;
        if (productId) productId.value = variantId;

        slides.forEach((slide, index) => {
          if (slide.dataset.variant !== undefined && slide.dataset.variant == variantId) {
            mainNode?._emblaApi?.scrollTo(index);
          }
        });
      });
    });
  };

  const initSection = (root) => {
    if (root.dataset.rtnuAbpMainCtaReady) return;
    root.dataset.rtnuAbpMainCtaReady = 'true';

    const { sectionId, formId, currencySymbol = '$' } = root.dataset;
    const form = formId ? root.querySelector(`#${CSS.escape(formId)}`) : null;

    initCarousel(root, sectionId);
    if (form) initVariants(root, form, sectionId, currencySymbol);
  };

  const initAll = () => document.querySelectorAll('[data-rtnu-abp-main-cta]').forEach(initSection);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
